import * as CL from '@design-system/component-library';
import { type JSX, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link, generatePath, useLocation, useNavigate } from 'react-router';
import { pushSubscriptionModifyEventToDataLayer } from '../../../common/analytics';
import { dsClass } from '../../../common/constants/dsClasses';
import { paths } from '../../../common/constants/pathVariables';
import {
  changeSubscriptionBillingAccount,
  fetchBillingAccounts,
  fetchContacts,
  postChangeOwnerAccount,
} from '../../../common/fetch';
import { useSearchParams } from '../../../common/hooks/useSearchParams';
import {
  I18nTextLinkEmbedded,
  billingAccountChangeCompletedMsg,
  billingAccountChangeFailedMsg,
  billingAccountMsg,
  cancelMsg,
  confirmMsg,
  contractPartyMsg,
  t,
  transferOfSubToAnotherBillingAccountMsg,
  userMsg,
} from '../../../common/i18n';
import { BillingAccount } from '../../../common/react-hook-form/fields/BillingAccount';
import { Contact as ContactInput } from '../../../common/react-hook-form/fields/Contact';
import { PurposeOfUse } from '../../../common/react-hook-form/fields/PurposeOfUse';
import {
  CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE,
  getDefaultBillingContactId,
  getDefaultDeliveryMethod,
} from '../../../common/utils/billingAccountUtils';
import { CompanySelector } from '../../../components/CompanySelector/CompanySelector';
import { GenericInfoDialog } from '../../../components/Dialogs/GenericInfoDialog';
import { getUserAccounts } from '../../../components/Header/dynamic/headerFunctions';
import { ChangeOwnerError } from '../../../generated/api/changeOwnerError';
import { SourceSystem } from '../../../generated/api/sourceSystem';
import { SubscriptionPbxDetails } from '../../../generated/api/subscriptionPbxDetails';
import { SubscriptionType } from '../../../generated/api/subscriptionType';
import { addNotification } from '../../../public/site/notificationStore.js';
import { HighlightField, HighlightedValue } from '../Components/HighlightedValue';
import type { BillingAccountsResponse } from '../../../generated/api/billingAccountsResponse';
import type { CompanyInfoResponse } from '../../../generated/api/companyInfoResponse';
import type { Contact } from '../../../generated/api/contact';
import type { GenericError } from '../../../generated/api/genericError';
import type { Subscription } from '../../../generated/api/subscription';
import type { AuthenticatedUser } from '../../../public/site/authState';
import type { HighlightedField } from '../Components/HighlightedValue';

import './ChangeContractOwnerAccordion.scss';

export interface ChangeContractOwnerViewProps {
  subscription: Subscription;
  companyInfo?: CompanyInfoResponse;
  billingAccountLink?: boolean;
  changedFields?: readonly HighlightedField[];
  projectedCompanyName?: string;
}

export interface ChangeContractOwnerAccordionEditorProps {
  authenticatedUser?: AuthenticatedUser;
  subscription: Subscription;
  billingAccounts?: BillingAccountsResponse;
  contacts?: Contact[];
  closeEditor: () => void;
  showBillingAccountChangeNotification?: boolean;
}

const loadBillingAccountsContacts = async (targetAccount: string) => {
  const [basForCompany, contactsForCompany] = await Promise.all([
    fetchBillingAccounts({ useSearchService: true, sourceSystem: SourceSystem.SFDC }, { mdmId: targetAccount }),
    fetchContacts({ offset: 0 }, { mdmId: targetAccount }),
  ]);
  return {
    basForCompany,
    contactsForCompany: contactsForCompany.contacts,
  };
};

const postChangeBillingAccount = async (targetBillingAccount: string, subscriptionId: string, mdmId: string) => {
  try {
    const subscriptionAction = await changeSubscriptionBillingAccount(subscriptionId, targetBillingAccount, mdmId);
    return {
      subscriptionAction,
    };
  } catch (error) {
    const errorResponse = await error.json();
    return {
      changeBillingAccountError: {
        status: error.status,
        ...(errorResponse[0] as GenericError),
      },
    };
  }
};

const postChangeContractOwner = async (
  targetAccount: string,
  targetBillingAccount: string,
  targetContact: Contact | undefined,
  purposeOfUse: string | undefined,
  subscriptionId: string,
  mdmId: string
) => {
  try {
    const subscriptionAction = await postChangeOwnerAccount(
      subscriptionId,
      {
        targetAccount,
        targetBillingAccount,
        targetContactDetails: {
          contactId: targetContact?.contactId,
          purposeOfUse,
        },
        subscriptionReference: targetContact?.person?.employeeNumber,
        costCenter: targetContact?.person?.costCenter,
      },
      mdmId
    );

    return {
      subscriptionAction,
    };
  } catch (error) {
    const errorResponse = await error.json();
    if (errorResponse[0].reason) {
      return {
        changeOwnerErrorResponse: errorResponse[0] as ChangeOwnerError,
      };
    }
    return {
      commonErrorResponse: errorResponse[0] as GenericError,
    };
  }
};

const ChangeContractOwnerErrorDialog = (props: { header: string; body: JSX.Element }) => {
  const [open, setOpen] = useState(true);
  return open ? (
    <GenericInfoDialog onCloseDialog={() => setOpen(false)} header={props.header} body={props.body} />
  ) : null;
};

interface ChangeOwnerErrorMessageElementProps {
  error: ChangeOwnerError | undefined | null;
  search: string;
}

const ChangeOwnerErrorMessageElement = ({ error, search }: ChangeOwnerErrorMessageElementProps) => {
  switch (error?.reason) {
    case ChangeOwnerError.ReasonEnum.INVALID_EPP_AGREEMENT:
      return (
        <ChangeContractOwnerErrorDialog
          key={error.reason}
          header={t.HJJ3('Contracting company can not be changed')}
          body={
            <>
              <p>
                {t.TYVC('Contracting company can not be changed, because: ')}
                <>
                  <li className={`${dsClass.MARGIN_LEFT_2}`}>
                    {t.WKIN(
                      'The target company does not have a corresponding Elisa Palvelupäätelaite (EPP) service agreement'
                    )}
                  </li>
                </>
              </p>
              <p>
                <I18nTextLinkEmbedded
                  translation={t.UJ8H('Create a [support case], if needed.')}
                  linkTo={paths.MESSAGE_CENTER_SUPPORT_CASE_NEW + search}
                />
              </p>
            </>
          }
        />
      );
    case ChangeOwnerError.ReasonEnum.EPP_OMA_BA:
      return (
        <ChangeContractOwnerErrorDialog
          key={error.reason}
          header={t.HJJ3('Contracting company can not be changed')}
          body={
            <>
              <p>
                {t.L9E5(
                  'Contracting company cannot be changed, because the user of the device has an active Elisa Oma Laitelasku agreement.'
                )}
              </p>
              <p>
                <I18nTextLinkEmbedded
                  translation={t.KOO8(
                    'If you want to transfer the user to another company, you can do that on [Users] page.'
                  )}
                  linkTo={paths.COMPANY_INFO_CONTACTS}
                />
              </p>
            </>
          }
        />
      );
    case ChangeOwnerError.ReasonEnum.VOICE_VAKIO_OR_RING:
      return (
        <CL.GridRow gutter={false} className="of-change-owner-accordion--data-row">
          <CL.GridCol colWidth={12}>
            <CL.Disclaimer
              title={t.HJJ3('Contracting company can not be changed')}
              icon={<CL.Icon icon="information" size="s" />}
              visible
            >
              <span>
                <I18nTextLinkEmbedded
                  translation={t.SX7R(
                    'Contracting company can not be changed, because additional service prevents the change. Create a [support case], if needed.'
                  )}
                  linkTo={paths.MESSAGE_CENTER_SUPPORT_CASE_NEW + search}
                />
              </span>
            </CL.Disclaimer>
          </CL.GridCol>
        </CL.GridRow>
      );
    case ChangeOwnerError.ReasonEnum.COLLECTION:
      return (
        <ChangeContractOwnerErrorDialog
          key={error.reason}
          header={t.HJJ3('Contracting company can not be changed')}
          body={
            <p>
              <I18nTextLinkEmbedded
                translation={t.W7H8('Please contact our [customer service] if needed.')}
                linkTo={paths.CONTACT_INFO}
              />
            </p>
          }
        />
      );
    default:
      return null;
  }
};

const validateSubscriptionForChangeOwner = (subscription: Subscription): ChangeOwnerError | null => {
  if (SubscriptionType.MOBILE === subscription.subscriptionType) {
    if (
      SubscriptionPbxDetails.PbxTypeEnum.VAKIO ===
        subscription.details?.mobile?.pbxConfiguration?.pbxConfigurationDetails?.pbxType &&
      !!subscription.details?.mobile?.pbxConfiguration?.corporateNumber &&
      !!subscription.details?.mobile?.pbxConfiguration?.extensionNumber
    ) {
      return {
        reason: ChangeOwnerError.ReasonEnum.VOICE_VAKIO_OR_RING,
      };
    } else if (
      SubscriptionPbxDetails.PbxTypeEnum.RING ===
      subscription.details?.mobile?.pbxConfiguration?.pbxConfigurationDetails?.pbxType
    ) {
      return {
        reason: ChangeOwnerError.ReasonEnum.VOICE_VAKIO_OR_RING,
      };
    }
  } else if (SubscriptionType.SPECIAL_NUMBER === subscription.subscriptionType) {
    return {
      reason: ChangeOwnerError.ReasonEnum.VOICE_VAKIO_OR_RING,
    };
  } else if (SubscriptionType.MOBILE_PBX_LITE === subscription.subscriptionType) {
    return {
      reason: ChangeOwnerError.ReasonEnum.VOICE_VAKIO_OR_RING,
    };
  } else if (SubscriptionType.MOBILE_PBX === subscription.subscriptionType) {
    return {
      reason: ChangeOwnerError.ReasonEnum.VOICE_VAKIO_OR_RING,
    };
  }
  return null;
};

const shouldDisableField = (error?: ChangeOwnerError | null) => {
  return ChangeOwnerError.ReasonEnum.VOICE_VAKIO_OR_RING === error?.reason;
};

export const ChangeContractOwnerEditor = ({
  authenticatedUser,
  billingAccounts,
  subscription,
  contacts,
  closeEditor,
  showBillingAccountChangeNotification = false,
}: ChangeContractOwnerAccordionEditorProps) => {
  const [targetAccountId, setTargetAccountId] = useState<string>();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const searchParams = useSearchParams<{ mdmId?: string }>();
  const [baResponseForTargetCompany, setBaResponseForTargetCompany] = useState<BillingAccountsResponse>();
  const [contactsForTargetCompnay, setContactsForTargetCompnay] = useState<Contact[]>();
  const [changeOwnerError, setChangeOwnerError] = useState<ChangeOwnerError>();

  const methods = useForm({
    defaultValues: {
      changeContractOwner: {
        targetBillingAccount: subscription.billingAccountId || '',
        targetContact: '',
        targetPurposeOfUse: subscription.subscriptionPurposeOfUse,
      },
      key: 0,
    },
  });

  const key = methods.watch('key');

  useEffect(() => {
    const { unsubscribe } = methods.watch(({ changeContractOwner }) => {
      if (changeContractOwner?.targetBillingAccount === CREATE_NEW_BILLING_ACCOUNT_OPTION_VALUE) {
        const params = new URLSearchParams(searchParams);
        params.append('subscriptionId', subscription.subscriptionId);
        const baResults = billingAccounts?.searchResults?.map(sr => sr.result);
        navigate(generatePath(`${paths.BILLING_ACCOUNTS_CREATE_NEW_BA}?${params}`), {
          state: {
            redirectPath: pathname + search,
            hideDisclaimerText: true,
            defaultBillingContactId: getDefaultBillingContactId(baResults),
            defaultDeliveryMethod: getDefaultDeliveryMethod(baResults),
          },
        });
      }
    });
    return () => unsubscribe();
  }, [methods, navigate, pathname, search, searchParams, subscription.subscriptionId, billingAccounts?.searchResults]);

  const isChangeContractOwner = !!targetAccountId && targetAccountId !== searchParams.mdmId;
  const isPurposeOfUseSubscription = !subscription.subscriptionContactName && !subscription.subscriptionContactId;
  const subscriptionValidationErrors = validateSubscriptionForChangeOwner(subscription);

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit(async values => {
          pushSubscriptionModifyEventToDataLayer('contract owner and billing account');
          if (isChangeContractOwner) {
            const selectedContact = contactsForTargetCompnay?.find(
              c => c.contactId === values.changeContractOwner.targetContact
            );
            const { changeOwnerErrorResponse, commonErrorResponse } = await postChangeContractOwner(
              targetAccountId,
              values.changeContractOwner.targetBillingAccount,
              selectedContact,
              isPurposeOfUseSubscription ? values.changeContractOwner.targetPurposeOfUse : undefined,
              subscription.subscriptionId,
              searchParams.mdmId || ''
            );
            if (changeOwnerErrorResponse) {
              setChangeOwnerError(changeOwnerErrorResponse);
            } else if (commonErrorResponse) {
              addNotification(t.ORRM('Submit change order failed'), 'error');
            } else {
              addNotification(t.E8T5('Submit change order successful'), 'success');
              navigate(`?mdmId=${searchParams.mdmId}`, { replace: true });
              closeEditor();
            }
          } else {
            const { changeBillingAccountError } = await postChangeBillingAccount(
              values.changeContractOwner.targetBillingAccount,
              subscription.subscriptionId,
              searchParams.mdmId || ''
            );
            if (changeBillingAccountError) {
              addNotification(t.S3DX(billingAccountChangeFailedMsg), 'error');
            } else {
              addNotification(t.CTBU(billingAccountChangeCompletedMsg), 'success');
              navigate(`?mdmId=${searchParams.mdmId}`, { replace: true });
              closeEditor();
            }
          }
        })}
      >
        <CL.Grid>
          <CL.GridRow gutter={false} className="of-change-owner-accordion--data-row">
            <CL.GridCol colWidth={6}>
              <CompanySelector
                labelText={t.HCIE('Contract party')}
                disabled={shouldDisableField(changeOwnerError || subscriptionValidationErrors)}
                userAccounts={getUserAccounts(authenticatedUser)}
                initialMdmId={searchParams.mdmId}
                readOnlyIncludeHeader={true}
                onInputChange={async (_, option) => {
                  setTargetAccountId(option.accountMasterId);
                  methods.setValue('changeContractOwner.targetBillingAccount', '');
                  methods.setValue('changeContractOwner.targetContact', '');
                  methods.setValue('key', key + 1);
                  const { basForCompany, contactsForCompany } = await loadBillingAccountsContacts(
                    option.accountMasterId || ''
                  );
                  setBaResponseForTargetCompany(basForCompany);
                  setContactsForTargetCompnay(contactsForCompany);
                }}
              />
            </CL.GridCol>
          </CL.GridRow>
          <ChangeOwnerErrorMessageElement error={changeOwnerError || subscriptionValidationErrors} search={search} />
          {isChangeContractOwner && (
            <CL.GridRow gutter={false} className="of-change-owner-accordion--data-row">
              <CL.GridCol colWidth={12}>
                <CL.Disclaimer icon={<CL.Icon icon="information" size="s" />} visible>
                  <span>
                    {isPurposeOfUseSubscription ? (
                      t.M16R(
                        'You are changing the contracting company. The billing account must also be updated according to the new contracting company.'
                      )
                    ) : (
                      <I18nTextLinkEmbedded
                        translation={t.CPDO(
                          'You are changing the contracting company. The billing account and user must also be updated according to the new contracting company. If you want to move the user to another company, you can do so on the [Users] page.'
                        )}
                        linkTo={paths.COMPANY_INFO_CONTACTS}
                      />
                    )}
                  </span>
                </CL.Disclaimer>
              </CL.GridCol>
            </CL.GridRow>
          )}
          <CL.GridRow gutter={false} className="of-change-owner-accordion--data-row">
            <CL.GridCol colWidth={6}>
              <BillingAccount
                name="changeContractOwner.targetBillingAccount"
                enableAddNewBa={!isChangeContractOwner}
                billingAccounts={
                  (isChangeContractOwner && baResponseForTargetCompany?.searchResults) ||
                  billingAccounts?.searchResults ||
                  []
                }
              />
            </CL.GridCol>
          </CL.GridRow>
          {showBillingAccountChangeNotification && (
            <CL.Disclaimer
              className={dsClass.MARGIN_BOTTOM_5}
              title={t.V2E6(transferOfSubToAnotherBillingAccountMsg)}
              icon={<CL.Icon icon="information" size="s" />}
              visible
            >
              <p>
                {t.AIUJ(
                  'Please note that, after the change, invoicing will still be based on two invoices for the next payment. Any supplementary services will be transferred with the main product.'
                )}
              </p>
            </CL.Disclaimer>
          )}
          {isChangeContractOwner && !isPurposeOfUseSubscription && (
            <CL.GridRow gutter={false} className="of-change-owner-accordion--data-row">
              <CL.GridCol colWidth={6}>
                <ContactInput
                  labelText={t.LUZM(userMsg)}
                  enableAddNewContact={false}
                  reference=""
                  name="changeContractOwner.targetContact"
                  contacts={(isChangeContractOwner && contactsForTargetCompnay) || contacts || []}
                />
              </CL.GridCol>
            </CL.GridRow>
          )}
          {isChangeContractOwner && isPurposeOfUseSubscription && (
            <CL.GridRow gutter={false} className="of-change-owner-accordion--data-row">
              <CL.GridCol colWidth={6}>
                <PurposeOfUse name="changeContractOwner.targetPurposeOfUse" />
              </CL.GridCol>
            </CL.GridRow>
          )}
        </CL.Grid>
        <CL.ButtonGroup align="left">
          <CL.Button type="submit">{t.QVYK(confirmMsg)}</CL.Button>
          <CL.Button
            type="reset"
            color="link"
            onClick={() => {
              methods.reset();
              setTargetAccountId(undefined);
              closeEditor();
            }}
          >
            {t.B2V1(cancelMsg)}
          </CL.Button>
        </CL.ButtonGroup>
      </form>
    </FormProvider>
  );
};

export const ChangeContractOwnerView = ({
  subscription,
  companyInfo,
  billingAccountLink,
  changedFields = [],
  projectedCompanyName,
}: ChangeContractOwnerViewProps) => {
  const { mdmId } = useSearchParams<{ mdmId: string }>();
  return (
    <CL.Grid>
      <CL.GridRow gutter={false} className="of-change-owner-accordion--data-row">
        <CL.GridCol colWidth={12}>
          <div>
            <span className={`${dsClass.FONT_STYLE_NORMAL} ${dsClass.FONT_WEIGHT_BOLD}`}>
              {t.HCIE(contractPartyMsg)}
            </span>
          </div>
          <HighlightedValue highlighted={changedFields.includes(HighlightField.CONTRACT_OWNER)}>
            {projectedCompanyName ?? companyInfo?.companyName ?? '—'}
          </HighlightedValue>
        </CL.GridCol>
      </CL.GridRow>
      <CL.GridRow gutter={false} className="of-change-owner-accordion--data-row">
        <CL.GridCol colWidth={12}>
          <div>
            <span className={`${dsClass.FONT_STYLE_NORMAL} ${dsClass.FONT_WEIGHT_BOLD}`}>
              {t.IFT9(billingAccountMsg)}
            </span>
          </div>
          {billingAccountLink && subscription.billingAccountDisplayId ? (
            <HighlightedValue highlighted={changedFields.includes(HighlightField.BILLING_ACCOUNT)}>
              <Link
                to={`${generatePath(paths.BILLING_ACCOUNT, { billingAccountId: subscription.billingAccountDisplayId })}?mdmId=${mdmId}`}
              >
                {subscription.billingAccountDisplayId}
              </Link>
            </HighlightedValue>
          ) : (
            <HighlightedValue highlighted={changedFields.includes(HighlightField.BILLING_ACCOUNT)}>
              {subscription.billingAccountDisplayId ?? '—'}
            </HighlightedValue>
          )}
        </CL.GridCol>
      </CL.GridRow>
    </CL.Grid>
  );
};
